import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { db } from '../firebase';
import { doc, getDoc, updateDoc, collection, onSnapshot, serverTimestamp, deleteDoc } from 'firebase/firestore';
import { useAuth } from '../AuthProvider';
import { useUI } from '../UIContext';
import PageWrapperAuto from '../components/PageWrapperAuto';
import { normalizeKey, normalizeType, keyRoot, normalizeTagsArray, normalizeTag, normalizeGenre, getNormalizedGenreOptions } from '../utils/normalize';
import AbcPreview from '../components/AbcPreview';
import { useUserPrefs } from '../UserPrefsProvider';
import { loadSetHybrid } from '../utils/loadSetHybrid';
import dexie from '../offlineDB';
import { getUserProfile, sendSetToUser } from '../utils/socialFirestore';
import TuneRecorder from '../components/TuneRecorder';
import AudioPlayerWithSpeed from '../components/AudioPlayerWithSpeed';
import { syncSetRecordings } from '../utils/syncSetRecordings';
import { addPendingWrite } from '../utils/flushPendingWrites';
import { getModeLabelsForEntity } from '../utils/modeLabels';
import InlineEditableTitle from '../components/InlineEditableTitle';
import DetailTagEditor from '../components/DetailTagEditor';
import DetailOrganizeReorderPanel from '../components/DetailOrganizeReorderPanel';
import ProgramSlotsEditorPanel from '../components/ProgramSlotsEditorPanel';
import AppSelect from '../components/AppSelect';
import PreviewModePill from '../components/PreviewModePill';
import DetailTimestampsFooter from '../components/DetailTimestampsFooter';
import ActionConfirmModal from '../components/ActionConfirmModal';
import SendExportModal from '../components/SendExportModal';
import InternalBacklinksPanel from '../components/InternalBacklinksPanel';
import { findInboundInternalLinks } from '../utils/internalNoteLinks';

export default function SetDetailPage() {
  const { user } = useAuth();
  const { prefs } = useUserPrefs();
  const { textStyle, inputStyle } = useUI();
  const { id: setId } = useParams();
  const location = useLocation();
const state = location.state || {};
const setTunes = state.setTunes || [];
const currentIndex = setTunes.findIndex(item => item.id === setId);
const parentSetTitle = state.setTitle || null;
const internalLinkFrom = state.internalLinkFrom || null;

  const navigate = useNavigate();
  const [setData, setSetData] = useState(null);
  const [allTunes, setAllTunes] = useState([]);
  const [showNotes, setShowNotes] = useState(false);
  const [allSets, setAllSets] = useState([]);
  const [confirmDeleteSet, setConfirmDeleteSet] = useState(false);
  const [showSendPicker, setShowSendPicker] = useState(false);
  const [isEditingSet, setIsEditingSet] = useState(false);
  const [showSetOrganizer, setShowSetOrganizer] = useState(false);
  const [setDraft, setSetDraft] = useState({
    title: '',
    notes: '',
    genre: '',
    rating: '',
    status: '',
    customTags: [],
    tunes: [],
    slots: [],
  });
  const [setItemSearchAny, setSetItemSearchAny] = useState('');
  const [setItemFilterGenre, setSetItemFilterGenre] = useState('');
  const [setItemFilterType, setSetItemFilterType] = useState('');
  const [setItemFilterKey, setSetItemFilterKey] = useState('');
  const [setItemTypeFilter, setSetItemTypeFilter] = useState('all');
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editTitleValue, setEditTitleValue] = useState('');
  const [setDragIndex, setSetDragIndex] = useState(null);
  const [programNewSlotName, setProgramNewSlotName] = useState('');
  const [programEditingSlotIndex, setProgramEditingSlotIndex] = useState(null);
  const recordingBlobUrlMapRef = useRef(new Map());
  const setNotesTextareaRef = useRef(null);
  const viewedSetIdRef = useRef(null);
  const [isCompactActions, setIsCompactActions] = useState(() => (
    typeof window !== 'undefined' ? window.innerWidth <= 560 : false
  ));
  const actionBtnStyle = {
    fontSize: 'var(--ui-compact-font)',
    padding: '4px 10px',
    lineHeight: 1.1,
  };


  useEffect(() => {
    if (!user) return;

    const unsubSets = onSnapshot(collection(db, 'users', user.uid, 'sets'), snapshot => {
      setAllSets(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    const unsubTunes = onSnapshot(collection(db, 'users', user.uid, 'tunes'), snapshot => {
      setAllTunes(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

const fetchSet = async () => {
  const data = await loadSetHybrid(user, setId);
  if (data) setSetData(data);
};


    fetchSet();
    return () => {
      unsubTunes();
      unsubSets();
    };

  }, [user, setId]);

useEffect(() => {
  if (!user || navigator.onLine) return;

  // Offline fallback
 dexie.sets.toArray().then(setList => {
    setAllSets(setList);
  });
  dexie.tunes.toArray().then(tuneList => {
    setAllTunes(tuneList);
  });
}, [user]);

useEffect(() => {
  if (!setData) return;
  setSetDraft({
    title: setData.title || '',
    notes: setData.notes || '',
    genre: setData.genre || '',
    rating: setData.rating || '',
    status: setData.status || '',
    customTags: Array.isArray(setData.customTags) ? setData.customTags : [],
    tunes: Array.isArray(setData.tunes) ? setData.tunes : [],
    slots: Array.isArray(setData.slots) ? setData.slots : [],
  });
  setEditTitleValue(setData.title || '');
}, [setData]);



async function handleDeleteSet() {
  if (!setData || !user) return;
  const id = setId;
  await deleteDoc(doc(db, 'users', user.uid, 'sets', id));
  await dexie.sets.delete(id);
  navigate(-1);
}

async function handleSendSet(recipientUid, contact) {
  const myProfile = await getUserProfile(user.uid);
  const tuneIds = (setData.tunes || []).filter(t => t.type !== 'set').map(t => t.id);
  await sendSetToUser(user.uid, myProfile, recipientUid, setId, setData.title, tuneIds);

  // Record sharedWith on the set
  const setRef = doc(db, 'users', user.uid, 'sets', setId);
  const setSnap = await getDoc(setRef);
  if (setSnap.exists()) {
    const existing = setSnap.data().sharedWith || [];
    if (!existing.some(s => s.uid === recipientUid)) {
      await updateDoc(setRef, {
        sharedWith: [...existing, {
          uid: recipientUid,
          displayName: contact?.displayName || '',
          username: contact?.username || '',
          sentAt: new Date().toISOString()
        }]
      });
      setSetData(prev => prev ? {
        ...prev,
        sharedWith: [...(prev.sharedWith || []), { uid: recipientUid, displayName: contact?.displayName || '', username: contact?.username || '' }]
      } : prev);
    }
  }
}

const resolveTune = (stub) => {
  if (stub.type === 'set') {
    const match = allSets.find(s => s.id === stub.id);
    return match ? { ...match, type: 'set', _entryType: 'set' } : { ...stub, _entryType: 'set' };
  } else {
    const match = allTunes.find(t => t.id === stub.id);
    return match ? { ...match, _entryType: 'tune' } : { ...stub, _entryType: stub.type || 'tune' };
  }
};

const getStableRecordingAudioUrl = (rec) => {
  if (rec?.url) return rec.url;
  if (!rec?.localBlob) return null;
  const key = rec.id || `${rec.createdAt || ''}:${rec.label || ''}`;
  const existing = recordingBlobUrlMapRef.current.get(key);
  if (existing) return existing;
  const nextUrl = URL.createObjectURL(rec.localBlob);
  recordingBlobUrlMapRef.current.set(key, nextUrl);
  return nextUrl;
};

const handleSetRecordingsUpdate = async (recordings) => {
  if (!setData || !user) return;
  const next = { ...setData, recordings };
  setSetData(next);
  await dexie.sets.update(setId, { recordings });
  await addPendingWrite({
    id: `${setId}__setRecordings__${Date.now()}`,
    type: 'update',
    collection: 'sets',
    docId: setId,
    data: { recordings }
  });
  try {
    await syncSetRecordings({ ...next, id: setId }, user);
  } catch (err) {
    console.warn('Set recording sync deferred:', err);
  }
};

useEffect(() => {
  const activeKeys = new Set(
    (setData?.recordings || []).map((r) => r.id || `${r.createdAt || ''}:${r.label || ''}`)
  );
  for (const [key, url] of recordingBlobUrlMapRef.current.entries()) {
    if (!activeKeys.has(key)) {
      URL.revokeObjectURL(url);
      recordingBlobUrlMapRef.current.delete(key);
    }
  }
}, [setData?.recordings]);

useEffect(() => () => {
  for (const [, url] of recordingBlobUrlMapRef.current.entries()) {
    URL.revokeObjectURL(url);
  }
  recordingBlobUrlMapRef.current.clear();
}, []);

useEffect(() => {
  const onResize = () => setIsCompactActions(window.innerWidth <= 560);
  window.addEventListener('resize', onResize);
  return () => window.removeEventListener('resize', onResize);
}, []);

useEffect(() => {
  if (!isEditingSet || !setNotesTextareaRef.current) return;
  const el = setNotesTextareaRef.current;
  el.style.height = 'auto';
  el.style.height = `${Math.max(44, el.scrollHeight)}px`;
}, [isEditingSet, setDraft.notes]);

useEffect(() => {
  if (!user || !setData?.id) return;
  if (viewedSetIdRef.current === setData.id) return;
  viewedSetIdRef.current = setData.id;
  const viewedAtIso = new Date().toISOString();

  (async () => {
    try {
      if (navigator.onLine) {
        await updateDoc(doc(db, 'users', user.uid, 'sets', setData.id), { lastViewedAt: serverTimestamp() });
      } else {
        await addPendingWrite({
          id: `${setData.id}__lastViewedAt__${Date.now()}`,
          type: 'update',
          collection: 'sets',
          docId: setData.id,
          data: { lastViewedAt: viewedAtIso }
        });
      }
      await dexie.sets.update(setData.id, { lastViewedAt: viewedAtIso });
      setSetData((prev) => (prev ? { ...prev, lastViewedAt: viewedAtIso } : prev));
    } catch (err) {
      console.warn('Failed to update set last viewed timestamp:', err);
    }
  })();
}, [user, setData?.id]);

if (!setData) {
  return (
    <PageWrapperAuto>
      <p style={{ textAlign: 'center' }}>
        ⚠️ Could not load set. You may be offline, not logged in, or the set was deleted.
      </p>
    </PageWrapperAuto>
  );
}

const getCollectionBadge = (entityMode) => (
  getModeLabelsForEntity(prefs?.appMode || 'tunes', entityMode || 'tunes')?.Collection?.toUpperCase() || 'SET'
);
const collectionBadge = getCollectionBadge(setData?.mode || 'tunes');
const modeLabels = getModeLabelsForEntity(prefs?.appMode || 'tunes', setData?.mode || 'tunes');
const backTargetLabel = modeLabels?.Collections || 'Sets';
const collectionLabel = modeLabels?.Collection || 'Set';
const collectionsLabel = modeLabels?.Collections || 'Sets';
const editingEntityLabel = setData?.isProgram ? 'Program' : collectionLabel;
const isNotesCollectionMode = (setData?.mode || 'tunes') === 'notes';
const canPrev = setTunes.length > 1 && currentIndex > 0;
const canNext = setTunes.length > 1 && currentIndex < setTunes.length - 1;
const compactActionStyle = isCompactActions
  ? { fontSize: '0.8rem', padding: '5px 8px', lineHeight: 1.05 }
  : undefined;
const goBack = () => {
  if (internalLinkFrom?.path) {
    navigate(internalLinkFrom.path);
    return;
  }
  navigate('/sets');
};
const goPrev = () => {
  if (!canPrev) return;
  const prevItem = setTunes[currentIndex - 1];
  if (prevItem?.type === 'set') {
    navigate(`/sets/${prevItem.id}`, { state });
    return;
  }
  navigate(`/tune/${prevItem.id}`, {
    state: {
      tune: prevItem,
      setId,
      setTitle: parentSetTitle,
      setTunes
    }
  });
};
const goNext = () => {
  if (!canNext) return;
  const nextItem = setTunes[currentIndex + 1];
  if (nextItem?.type === 'set') {
    navigate(`/sets/${nextItem.id}`, { state });
    return;
  }
  navigate(`/tune/${nextItem.id}`, {
    state: {
      tune: nextItem,
      setId,
      setTitle: parentSetTitle,
      setTunes
    }
  });
};
const setItemKeyGroups = {};
allTunes.forEach((t) => {
  const nk = normalizeKey(t.key);
  if (!nk) return;
  const root = keyRoot(nk);
  if (!setItemKeyGroups[root]) setItemKeyGroups[root] = new Set();
  setItemKeyGroups[root].add(nk);
});
const notesModeTuneCandidates = allTunes.filter((t) => ((t?.mode || 'tunes') === 'notes') || normalizeType(t?.type) === 'note');
const notDeleted = (item) => !(item?.deletedAt || item?.isDeleted || item?.deleted || item?.removedAt || item?.trashedAt);
const folderBacklinks = findInboundInternalLinks({
  targetType: 'folder',
  targetId: setId,
  notes: notesModeTuneCandidates.filter(notDeleted),
  folders: allSets.filter((s) => (s?.mode || 'tunes') === 'notes').filter(notDeleted),
});
const setGenreOptions = getNormalizedGenreOptions((isNotesCollectionMode ? notesModeTuneCandidates : allTunes).map((t) => t.genre));
const programGenreOptions = getNormalizedGenreOptions(allTunes.map((t) => t.genre));
const setTypeOptions = [...new Set((isNotesCollectionMode ? notesModeTuneCandidates : allTunes).map((t) => normalizeType(t.type)).filter(Boolean))].sort();
const setTypeFilterOptions = isNotesCollectionMode
  ? [
    { value: 'all', label: `Notes & ${collectionsLabel}` },
    { value: 'note', label: 'Notes only' },
    { value: 'set', label: `${collectionsLabel} only` },
  ]
  : [
    { value: 'all', label: 'Tunes & Sets' },
    { value: 'tune', label: 'Tunes only' },
    { value: 'set', label: `${collectionsLabel} only` },
  ];
const isNoteCandidate = (item) => ((item?.mode || 'tunes') === 'notes') || normalizeType(item?.type) === 'note';
const isTuneCandidate = (item) => !isNoteCandidate(item);
const combinedSetItems = [
  ...allTunes
    .filter((t) => (isNotesCollectionMode ? isNoteCandidate(t) : isTuneCandidate(t)))
    .map((t) => ({ ...t, itemType: isNotesCollectionMode ? 'note' : 'tune' })),
  ...allSets
    .filter((s) => s.id !== setId)
    .filter((s) => (isNotesCollectionMode ? (s.mode || 'tunes') === 'notes' : (s.mode || 'tunes') !== 'notes'))
    .map((s) => ({ ...s, itemType: 'set' }))
];
const filteredSetItems = combinedSetItems.filter((item) => {
  if (setItemTypeFilter !== 'all' && item.itemType !== setItemTypeFilter) return false;
  if (isNotesCollectionMode && item.itemType === 'tune') return false;
  const nk = normalizeKey(item.key || '');
  const matchAny = !setItemSearchAny || Object.values(item).some((val) =>
    typeof val === 'string' && val.toLowerCase().includes(setItemSearchAny.toLowerCase())
  );
  const matchType = isNotesCollectionMode || item.itemType === 'set' || !setItemFilterType || normalizeType(item.type) === normalizeType(setItemFilterType);
  const matchKey = isNotesCollectionMode || item.itemType === 'set' || !setItemFilterKey
    || nk === setItemFilterKey
    || (setItemFilterKey.startsWith('root:') && keyRoot(nk) === setItemFilterKey.replace('root:', ''));
  const matchGenre = isNotesCollectionMode || item.itemType === 'set' || !setItemFilterGenre || normalizeGenre(item.genre) === setItemFilterGenre;
  return matchAny && matchType && matchKey && matchGenre;
});
const normalizeProgramSlots = (slots = []) => (
  slots
    .map((slot, index) => ({ name: (slot?.name || '').trim(), order: index }))
    .filter((slot) => slot.name)
);
const moveSetDraftItem = (index, direction) => {
  setSetDraft((prev) => {
    const next = [...(prev.tunes || [])];
    const target = index + direction;
    if (target < 0 || target >= next.length) return prev;
    const temp = next[index];
    next[index] = next[target];
    next[target] = temp;
    const updated = { ...prev, tunes: next };
    saveSetEdits(updated);
    return updated;
  });
};
const removeSetDraftItem = (item, index) => {
  setSetDraft((prev) => {
    const updated = {
      ...prev,
      tunes: (prev.tunes || []).filter((t, i) => !(i === index && t.id === item.id && t.type === item.type))
    };
    saveSetEdits(updated);
    return updated;
  });
};
const addSetDraftItem = (item) => {
  const exists = (setDraft.tunes || []).some((existing) => existing.id === item.id && existing.type === item.itemType);
  if (exists) return;
  const newEntry = {
    id: item.id,
    title: item.title || '',
    type: item.itemType,
    key: item.key || '',
    tuneType: item.type || '',
    genre: item.genre || '',
  };
  setSetDraft((prev) => {
    const updated = {
      ...prev,
      title: prev.title || item.title || '',
      tunes: [...(prev.tunes || []), newEntry],
    };
    saveSetEdits(updated);
    return updated;
  });
};
const handleSetDraftDragStart = (index) => {
  setSetDragIndex(index);
};
const handleSetDraftDrop = (targetIndex) => {
  if (setDragIndex === null || targetIndex === setDragIndex) {
    setSetDragIndex(null);
    return;
  }
  setSetDraft((prev) => {
    const next = [...(prev.tunes || [])];
    const [moved] = next.splice(setDragIndex, 1);
    next.splice(targetIndex, 0, moved);
    const updated = { ...prev, tunes: next };
    saveSetEdits(updated);
    return updated;
  });
  setSetDragIndex(null);
};
const moveProgramSlot = (index, direction) => {
  setSetDraft((prev) => {
    const next = [...(prev.slots || [])];
    const target = index + direction;
    if (target < 0 || target >= next.length) return prev;
    const temp = next[index];
    next[index] = next[target];
    next[target] = temp;
    const updated = { ...prev, slots: normalizeProgramSlots(next) };
    saveSetEdits(updated);
    return updated;
  });
};
const removeProgramSlot = (slot, index) => {
  setSetDraft((prev) => {
    const next = (prev.slots || []).filter((_, i) => i !== index);
    const updated = { ...prev, slots: normalizeProgramSlots(next) };
    saveSetEdits(updated);
    return updated;
  });
};
const addProgramSlot = () => {
  const name = (programNewSlotName || '').trim();
  if (!name) return;
  setSetDraft((prev) => {
    const updated = {
      ...prev,
      slots: normalizeProgramSlots([...(prev.slots || []), { name }]),
    };
    saveSetEdits(updated);
    return updated;
  });
  setProgramNewSlotName('');
};
const handleProgramSlotDrop = (targetIndex) => {
  if (setDragIndex === null || targetIndex === setDragIndex) {
    setSetDragIndex(null);
    return;
  }
  setSetDraft((prev) => {
    const next = [...(prev.slots || [])];
    const [moved] = next.splice(setDragIndex, 1);
    next.splice(targetIndex, 0, moved);
    const updated = { ...prev, slots: normalizeProgramSlots(next) };
    saveSetEdits(updated);
    return updated;
  });
  setSetDragIndex(null);
};
const updateProgramSlotName = (index, name, shouldPersist = false) => {
  setSetDraft((prev) => {
    const next = [...(prev.slots || [])];
    if (!next[index]) return prev;
    next[index] = { ...next[index], name };
    const updated = { ...prev, slots: next };
    if (shouldPersist) {
      const persisted = { ...prev, slots: normalizeProgramSlots(next) };
      saveSetEdits(persisted);
      return persisted;
    }
    return updated;
  });
};
const saveSetEdits = async (draftOverride = null) => {
  if (!user || !setData?.id) return;
  const draft = draftOverride || setDraft;
  const updatedData = {
    title: (draft.title || '').trim() || 'Untitled',
    notes: (draft.notes || '').trim(),
    genre: (draft.genre || '').trim(),
    rating: draft.rating || '',
    status: setData?.isProgram ? '' : (draft.status || ''),
    customTags: normalizeTagsArray(draft.customTags || []),
    ...(setData?.isProgram
      ? { slots: normalizeProgramSlots(draft.slots || []) }
      : { tunes: draft.tunes || [] }),
    updatedAt: new Date().toISOString()
  };
  const ref = doc(db, 'users', user.uid, 'sets', setData.id);
  const prefsRef = doc(db, 'users', user.uid, 'preferences', 'settings');
  try {
    const prefsSnap = await getDoc(prefsRef);
    const prefsData = prefsSnap.exists() ? prefsSnap.data() : {};
    const existingPrefsTags = Array.isArray(prefsData.customTags) ? prefsData.customTags : [];
    const mergedTags = Array.from(new Set([
      ...normalizeTagsArray(draft.customTags || []),
      ...existingPrefsTags.map(normalizeTag)
    ]));
    if (navigator.onLine) {
      await updateDoc(prefsRef, { customTags: mergedTags });
      await updateDoc(ref, updatedData);
    } else {
      await addPendingWrite({
        id: 'settings',
        type: 'update',
        collection: 'preferences',
        docId: 'settings',
        data: { customTags: mergedTags }
      });
      await addPendingWrite({
        id: setData.id,
        type: 'update',
        collection: 'sets',
        docId: setData.id,
        data: { id: setData.id, ...updatedData }
      });
    }
    await dexie.sets.put({ id: setData.id, ...(setData || {}), ...updatedData });
    setSetData((prev) => ({ ...(prev || {}), ...updatedData }));
  } catch (err) {
    console.error('Failed to save set edits:', err);
    alert('Failed to save set updates.');
  }
};
const persistSetUpdates = async (partial) => {
  if (!user || !setData?.id) return;
  const nextPartial = { ...(partial || {}) };
  if (Object.prototype.hasOwnProperty.call(nextPartial, 'title')) {
    nextPartial.title = (nextPartial.title || '').trim() || 'Untitled';
  }
  const updates = { ...nextPartial, updatedAt: new Date().toISOString() };
  try {
    const ref = doc(db, 'users', user.uid, 'sets', setData.id);
    if (navigator.onLine) {
      await updateDoc(ref, updates);
    } else {
      await addPendingWrite({
        id: `${setData.id}__update__${Date.now()}`,
        type: 'update',
        collection: 'sets',
        docId: setData.id,
        data: updates
      });
    }
    await dexie.sets.put({ id: setData.id, ...(setData || {}), ...updates });
    setSetData((prev) => ({ ...(prev || {}), ...updates }));
  } catch (err) {
    console.error('Failed to update set:', err);
  }
};

  return (
    <PageWrapperAuto>

      {parentSetTitle && (
  <div style={{ marginTop: '12px', marginBottom: '12px', textAlign: 'center' }}>
    <h2 style={{ ...textStyle, textTransform: 'uppercase', marginBottom: '8px' }}>
      Set: {parentSetTitle}
    </h2>
  </div>
)}

<div className="top-detail-actions-bar detail-actions-bar">
  <div className="top-detail-actions-side top-detail-actions-left detail-actions-side detail-actions-left">
    <button
      className="btn-subtle search-control-btn top-detail-action-btn detail-action-btn"
      onClick={goPrev}
      disabled={!canPrev}
      style={{ ...(compactActionStyle || {}), ...(!canPrev ? { opacity: 0.45, cursor: 'not-allowed' } : {}) }}
      title="Previous"
    >
      ← Prev
    </button>
    <button
      className="btn-subtle search-control-btn top-detail-action-btn detail-action-btn"
      onClick={goBack}
      style={compactActionStyle}
      title={`Back to ${internalLinkFrom?.title || backTargetLabel}`}
    >
      ← {internalLinkFrom?.title || backTargetLabel}
    </button>
  </div>
  <div className="top-detail-actions-side top-detail-actions-right detail-actions-side detail-actions-right">
    <button
      className="btn-subtle search-control-btn top-detail-action-btn detail-action-btn"
      onClick={() => setShowSendPicker(true)}
      style={compactActionStyle}
      title="Send"
    >
      {isCompactActions ? '📤' : '📤 Send / Export'}
    </button>
    <button
      className="btn-subtle search-control-btn top-detail-action-btn detail-action-btn"
      onClick={goNext}
      disabled={!canNext}
      style={{ ...(compactActionStyle || {}), ...(!canNext ? { opacity: 0.45, cursor: 'not-allowed' } : {}) }}
      title="Next"
    >
      Next →
    </button>
  </div>
</div>

      <div style={{ textAlign: 'center', marginBottom: '0.2rem' }}>
        <PreviewModePill variant={
          setData.isProgram
            ? 'program-template'
            : (collectionBadge === 'FOLDER' ? 'folder' : 'set')
        }>
          {setData.isProgram ? 'PROGRAM TEMPLATE' : collectionBadge}
        </PreviewModePill>
      </div>
      <InlineEditableTitle
        isEditing={isEditingTitle}
        value={editTitleValue}
        displayValue={setData.title || 'Untitled'}
        onChange={setEditTitleValue}
        onStartEditing={() => {
          setEditTitleValue(setData.title || '');
          setIsEditingTitle(true);
        }}
        onCommit={async () => {
          await persistSetUpdates({ title: editTitleValue });
          setIsEditingTitle(false);
        }}
        onCancel={() => {
          setEditTitleValue(setData.title || '');
          setIsEditingTitle(false);
        }}
        rowClassName="inline-editable-title-row detail-title-row"
        displayClassName="inline-editable-title-display detail-title-display detail-title-shell detail-title-heading"
        inputClassName="search-input inline-editable-title-input detail-title-input detail-title-shell detail-title-heading"
        inputStyle={{ ...inputStyle, boxSizing: 'border-box' }}
      />
      <DetailTagEditor
        tags={setData.customTags || []}
        onChange={(tags) => persistSetUpdates({ customTags: tags })}
        suggestions={prefs?.customTags || []}
        inputStyle={inputStyle}
      />
      {setData.fromProgram && setData.fromProgramTitle && (
        <p style={{ margin: '2px 0', fontSize: '0.85rem' }}>
          <PreviewModePill variant="program" style={{ marginRight: '6px' }}>PROGRAM</PreviewModePill>
          <span
            style={{ color: 'var(--text-link)', cursor: 'pointer', textDecoration: 'underline' }}
            onClick={() => navigate(`/sets/${setData.fromProgram}`)}
          >
            {setData.fromProgramTitle}
          </span>
        </p>
      )}
      
      {setData.genre && (
        <p style={{ margin: '2px 0', fontSize: '0.9rem' }}><strong>Genre:</strong> {setData.genre}</p>
      )}

      {/* Rating and Tags */}
        <div style={{ marginBottom: '0.75rem' }}>
          {!isEditingSet && setData.rating && (
            <div style={{ fontSize: '1rem', marginBottom: '1rem' }}>
              <strong>Rating: </strong> <span style={{ fontSize: '1.2rem' }}>{setData.rating}</span>
            </div>
          )}
          {!isEditingSet && !setData.isProgram && setData.status && (
            <div style={{ fontSize: '0.95rem', marginBottom: '0.55rem' }}>
              <strong>Status:</strong> {setData.status}
            </div>
          )}

      {setData.source && (
        <div style={{ fontSize: '0.9rem', marginTop: '0.5rem' }}>
          <strong>Source:</strong>{' '}
          {setData.importedFrom?.uid ? (
            <span
              style={{ color: 'var(--text-link)', textDecoration: 'underline', cursor: 'pointer' }}
              onClick={() => navigate(`/sets?sentBy=${setData.importedFrom.uid}&sentByName=${encodeURIComponent(setData.importedFrom.displayName || '')}`)}
            >
              {setData.source}
            </span>
          ) : (
            setData.source
          )}
        </div>
      )}

      {Array.isArray(setData.sharedWith) && setData.sharedWith.length > 0 && (
        <div style={{ fontSize: '0.85rem', marginTop: '0.5rem', color: 'var(--text-muted)' }}>
          <strong>Shared with:</strong>{' '}
          {setData.sharedWith.map((s, i) => (
            <span key={s.uid}>
              {i > 0 && ', '}
              <span
                style={{ color: 'var(--text-link)', textDecoration: 'underline', cursor: 'pointer' }}
                onClick={() => navigate(`/user/${s.uid}`)}
              >
                {s.displayName || s.username || 'Unknown'}
              </span>
            </span>
          ))}
        </div>
      )}

      {!isEditingSet && setData.notes && setData.notes.trim() !== '' && (
        <div style={{ marginTop: '0.75rem', textAlign: 'center' }}>
          <div
            onClick={() => setShowNotes(v => !v)}
            style={{
              cursor: 'pointer',
              fontWeight: 'bold',
              fontSize: '1rem',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.3rem',
              color: 'var(--text-primary)'
            }}
          >
            <span>{showNotes ? '▼' : '▶'} Notes:</span>
          </div>

          {showNotes && (
<div
  style={{
    marginTop: '0.5rem',
    display: 'flex',
    justifyContent: 'center',
  }}
>
  <div
    style={{
      textAlign: 'left',
      whiteSpace: 'pre-wrap',
      maxWidth: '400px',
      fontSize: '0.9rem',
      color: '#ccc',
      padding: '0 1rem',
    }}
  >
    {setData.notes}
  </div>
</div>

          )}
        </div>
      )}
      {isNotesCollectionMode && (
        <InternalBacklinksPanel
          backlinks={folderBacklinks}
          onNavigate={(item) => navigate(item.href, {
            state: {
              internalLinkFrom: {
                path: location.pathname,
                title: setData?.title || 'Folder',
              },
            },
          })}
        />
      )}

        </div>

      <div style={{ display: 'flex', justifyContent: 'center', gap: 'var(--ui-filter-grid-gap)', marginBottom: '12px', flexWrap: 'wrap' }}>
        {setData.isProgram ? (
          <button className="btn-subtle search-control-btn btn-program-action" style={actionBtnStyle} onClick={() => navigate(`/sets/${setId}/fill`)}>Use Program</button>
        ) : setData.fromProgram ? (
          <button className="btn-subtle" style={actionBtnStyle} onClick={() => navigate(`/sets/${setData.fromProgram}/fill`, {
            state: { editingSetId: setId, editingSetTitle: setData.title, existingTunes: setData.tunes }
          })}>Edit Program Set</button>
        ) : null}
      </div>


      {setData.isProgram && Array.isArray(setData.slots) && setData.slots.length > 0 && (
        <>
          <h3 style={{ marginBottom: '0.5rem' }}>Slots ({setData.slots.length})</h3>
          <ul className="card-list" style={{ marginBottom: '1.5rem' }}>
            {(isEditingSet ? (setDraft.slots || []) : setData.slots).map((slot, i) => (
              <li key={i} className="card" style={{ padding: '10px 12px' }}>
                <strong>{i + 1}. {slot.name}</strong>
              </li>
            ))}
          </ul>
        </>
      )}

      {!setData.isProgram && <ul className="card-list">
        {((isEditingSet ? setDraft.tunes : setData.tunes) || []).map((stub, i) => {
          const tune = resolveTune(stub);
          return (
           <li
  key={i}
  className="card"
onClick={() => {
  if (stub.type === 'note') { navigate(`/tune/${tune.id}`); return; }
  const resolvedTunes = ((isEditingSet ? setDraft.tunes : setData.tunes) || []).map(resolveTune);
  if (stub.type === 'set') {
    navigate(`/sets/${tune.id}`, {
      state: {
        setTunes: resolvedTunes,
        setTitle: setData.title,
      },
    });
  } else {
    navigate(`/tune/${tune.id}`, {
      state: {
        tune,
        setId,
        setTitle: setData.title,
        setTunes: resolvedTunes,
      },
    });
  }
}}

  style={{
    cursor: stub.type === 'note' ? 'default' : 'pointer',
  }}
>
  <div
    style={{
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '1rem',
    }}
  >
    <div style={{ flex: '1 1 200px', minWidth: '160px' }}>
      <div className="preview-pill-inline-row">
        <div className="preview-pill-title-group">
          <strong style={{ fontSize: '1rem' }}>
            {(stub.slotName || stub.isExtra) && (
              <span style={{ color: 'var(--text-muted)', fontWeight: 'normal', fontSize: '0.85rem' }}>
                {stub.isExtra ? 'Added' : stub.slotName} —&nbsp;
              </span>
            )}
            <>{tune.title}{stub.textNote && <span style={{ fontWeight: 'normal', fontSize: '0.85rem', color: 'var(--text-muted)' }}> ({stub.textNote})</span>}</>
          </strong>
          {stub.type === 'set' && (
            <PreviewModePill context="inline" variant={getCollectionBadge(tune?.mode || 'tunes') === 'FOLDER' ? 'folder' : 'set'}>
              {getCollectionBadge(tune?.mode || 'tunes')}
            </PreviewModePill>
          )}
        </div>
        {stub.type === 'set' ? (
          <span className="preview-pill-suffix" style={{ fontSize: '0.85rem', color: '#aaa' }}>
            – Includes:{' '}
            {Array.isArray(tune.tunes) && tune.tunes.length > 0
              ? tune.tunes.map(child => child.title).filter(Boolean).join(', ')
              : '0 tunes'}
          </span>
        ) : (
          <span className="preview-pill-suffix" style={{ fontSize: '1rem', color: 'var(--text-secondary)' }}>
            – {[tune.tuneType || tune.type, tune.key].filter(Boolean).join(' / ')}
          </span>
        )}
      </div>

    {tune.quickNote && (
      <div
        style={{
          marginTop: '4px',
          fontSize: '0.85em',
          color: '#888',
          fontStyle: 'italic',
        }}
      >
        {tune.quickNote}
      </div>
    )}

    {prefs?.showLyricsInSets && tune.lyrics && (
      <div
        style={{
          marginTop: '4px',
          fontSize: '0.85em',
          color: '#888',
          whiteSpace: 'pre-line',
          lineHeight: '1.2em',
          maxHeight: '2.6em',
          overflow: 'hidden',
        }}
      >
        {tune.lyrics.split('\n').slice(0, 2).join('\n')}
      </div>
    )}

    </div>

  {prefs?.showAbcInSets && tune.abc && (
    <div style={{ flex: '0 0 auto', overflow: 'hidden', height: '50px' }}>
      <AbcPreview abc={tune.abc} width={320} scale={0.55} />
    </div>
  )}

  </div>
</li>

          );
        })}
      </ul>}

      {!setData.isProgram && Array.isArray(setData.recordings) && setData.recordings.length > 0 && (
        <div style={{ marginTop: '0.75rem', textAlign: 'center' }}>
          <h4 style={{ marginBottom: '0.5rem' }}>Set Recordings</h4>
          <ul style={{ listStyleType: 'none', paddingLeft: 0, maxWidth: '420px', margin: '0 auto' }}>
            {setData.recordings.map((rec, idx) => (
              <li key={`${rec.id || 'rec'}-${idx}`} style={{ marginBottom: '0.75rem' }}>
                <div style={{ fontSize: '0.9rem', marginBottom: '4px' }}>{rec.label?.trim() || `Recording ${idx + 1}`}</div>
                {getStableRecordingAudioUrl(rec) ? (
                  <AudioPlayerWithSpeed src={getStableRecordingAudioUrl(rec)} compact />
                ) : (
                  <em style={{ color: 'var(--text-muted)' }}>Recording file not available on this device yet</em>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {!setData.isProgram && (
        <div style={{ width: '100%', maxWidth: '300px', margin: '0.35rem auto 0.2rem', textAlign: 'center' }}>
          <h4 style={{ margin: '0 0 0.2rem 0' }}>Add Recording</h4>
          <TuneRecorder
            tune={{ title: setData.title || 'Untitled' }}
            recordings={setData.recordings || []}
            onUpdateRecordings={handleSetRecordingsUpdate}
            containerStyle={{ marginTop: 0 }}
          />
        </div>
      )}

      <div style={{ maxWidth: '860px', margin: '1rem auto 0.7rem', textAlign: 'center' }}>
        <button
          className="btn-subtle search-control-btn detail-header-pill detail-header-pill-editing"
          onClick={() => {
            setIsEditingSet((prev) => {
              const next = !prev;
              setShowSetOrganizer(next);
              return next;
            });
          }}
          aria-expanded={isEditingSet}
        >
          <span className="detail-header-pill-caret">{isEditingSet ? '▼' : '▶'}</span>
          {isEditingSet ? `Done Editing ${editingEntityLabel}` : `Edit ${editingEntityLabel}`}
        </button>

        {isEditingSet && (
          <div className="detail-pill-content" style={{ marginTop: '8px' }}>
            <div style={{ maxWidth: '760px', margin: '0 auto 0.9rem', display: 'grid', gap: '8px' }}>
              {setData.isProgram && (
                <AppSelect
                  className="compact-action-select"
                  value={setDraft.genre || ''}
                  onChange={(value) => {
                    setSetDraft((prev) => {
                      const updated = { ...prev, genre: value || '' };
                      saveSetEdits(updated);
                      return updated;
                    });
                  }}
                  options={[
                    { value: '', label: 'Genre' },
                    ...programGenreOptions.map((genre) => ({ value: genre, label: genre }))
                  ]}
                  placeholder="Genre"
                  active={Boolean(setDraft.genre)}
                />
              )}
              <textarea
                ref={setNotesTextareaRef}
                className="search-input add-entry-textarea add-notes-field"
                rows={1}
                style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', minHeight: '44px', overflow: 'hidden', resize: 'none', gridColumn: '1 / -1' }}
                value={setDraft.notes || ''}
                onChange={(e) => setSetDraft((prev) => ({ ...prev, notes: e.target.value }))}
                onBlur={() => saveSetEdits()}
                placeholder="Notes"
              />
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: setData.isProgram ? 'minmax(0, 1fr)' : 'repeat(2, minmax(0, 1fr))',
                  gap: '8px',
                  width: '100%',
                  gridColumn: '1 / -1',
                  minWidth: 0
                }}
              >
                <AppSelect
                  className="compact-action-select"
                  value={setDraft.rating || ''}
                  onChange={(value) => {
                    setSetDraft((prev) => {
                      const updated = { ...prev, rating: value || '' };
                      saveSetEdits(updated);
                      return updated;
                    });
                  }}
                  options={[
                    { value: '★', label: '★' },
                    { value: '★★', label: '★★' },
                    { value: '★★★', label: '★★★' },
                    { value: '★★★★', label: '★★★★' },
                    { value: '★★★★★', label: '★★★★★' },
                  ]}
                  placeholder="Rating"
                  active={Boolean(setDraft.rating)}
                />
                {!setData.isProgram && (
                  <AppSelect
                    className="compact-action-select"
                    value={setDraft.status || ''}
                    onChange={(value) => {
                      setSetDraft((prev) => {
                        const updated = { ...prev, status: value || '' };
                        saveSetEdits(updated);
                        return updated;
                      });
                    }}
                    options={(modeLabels?.statusOptions || [])
                      .filter((opt) => opt && !String(opt).startsWith('Status'))
                      .map((opt) => ({ value: opt, label: opt }))}
                    placeholder="Status"
                    active={Boolean(setDraft.status)}
                  />
                )}
              </div>
            </div>

            <button
              className={`btn-subtle search-control-btn detail-organize-toggle-btn ${showSetOrganizer ? 'search-control-active' : ''}`}
              onClick={() => setShowSetOrganizer((v) => !v)}
            >
              {showSetOrganizer ? '▲ Hide Organize/Add Items' : '▼ Organize/Add Items'}
            </button>

            {setData.isProgram ? (
              <ProgramSlotsEditorPanel
                visible={showSetOrganizer}
                slots={setDraft.slots || []}
                inputStyle={inputStyle}
                actionBtnStyle={actionBtnStyle}
                newSlotName={programNewSlotName}
                onNewSlotNameChange={setProgramNewSlotName}
                onAddSlot={addProgramSlot}
                onDragStart={handleSetDraftDragStart}
                onDrop={handleProgramSlotDrop}
                onDragEnd={() => setSetDragIndex(null)}
                onMoveUp={(index) => moveProgramSlot(index, -1)}
                onMoveDown={(index) => moveProgramSlot(index, 1)}
                onRemove={removeProgramSlot}
                editingSlotIndex={programEditingSlotIndex}
                onStartEditingSlot={setProgramEditingSlotIndex}
                onChangeSlotName={(index, value) => updateProgramSlotName(index, value)}
                onCommitSlotName={(index, value) => {
                  updateProgramSlotName(index, value, true);
                  setProgramEditingSlotIndex(null);
                }}
                onCancelEditingSlot={() => setProgramEditingSlotIndex(null)}
              />
            ) : (
              <DetailOrganizeReorderPanel
                visible={showSetOrganizer}
                items={setDraft.tunes || []}
                availableItems={filteredSetItems}
                inputStyle={inputStyle}
                actionBtnStyle={actionBtnStyle}
                searchValue={setItemSearchAny}
                onSearchChange={setSetItemSearchAny}
                searchPlaceholder={isNotesCollectionMode ? 'Search notes' : 'Search any field'}
                searchRowControl={isNotesCollectionMode ? (
                  <AppSelect
                    className="compact-action-select"
                    value={setItemTypeFilter}
                    onChange={setSetItemTypeFilter}
                    options={setTypeFilterOptions}
                    placeholder={`Notes & ${collectionsLabel}`}
                    active={setItemTypeFilter !== 'all'}
                  />
                ) : null}
                onDragStart={handleSetDraftDragStart}
                onDrop={handleSetDraftDrop}
                onDragEnd={() => setSetDragIndex(null)}
                onMoveUp={(index) => moveSetDraftItem(index, -1)}
                onMoveDown={(index) => moveSetDraftItem(index, 1)}
                onRemove={removeSetDraftItem}
                renderItemLabel={(item) => (
                  <>
                    {item.title}
                    {isNotesCollectionMode
                      ? ''
                      : (item.type === 'set' ? ` (${collectionLabel})` : ((item.tuneType || item.type || item.key) ? ` (${[item.tuneType || item.type, item.key].filter(Boolean).join(', ')})` : ''))}
                  </>
                )}
                renderAvailableItemLabel={(item) => (
                  <>{item.title}{(!isNotesCollectionMode && item.itemType === 'set') ? ` (${collectionLabel})` : ''}</>
                )}
                getItemKey={(item, index) => `${item.id}-${item.type}-${index}`}
                getAvailableItemKey={(item) => `${item.itemType}-${item.id}`}
                isItemInSelection={(item) => (setDraft.tunes || []).some((existing) => existing.id === item.id && existing.type === item.itemType)}
                onAddItem={addSetDraftItem}
                filterControls={(
                  <>
                    {!isNotesCollectionMode && (
                      <AppSelect
                        className="compact-action-select"
                        value={setItemTypeFilter}
                        onChange={setSetItemTypeFilter}
                        options={setTypeFilterOptions}
                        placeholder="Item scope"
                        active={setItemTypeFilter !== 'all'}
                      />
                    )}
                    <AppSelect
                      className="compact-action-select"
                      value={setItemFilterGenre}
                      onChange={setSetItemFilterGenre}
                      options={[
                        { value: '', label: 'All categories' },
                        ...setGenreOptions.map((g) => ({ value: g, label: g }))
                      ]}
                      placeholder="All categories"
                      active={Boolean(setItemFilterGenre)}
                    />
                    <AppSelect
                      className="compact-action-select"
                      value={setItemFilterType}
                      onChange={setSetItemFilterType}
                      options={[
                        { value: '', label: 'All types' },
                        ...setTypeOptions.map((type) => ({ value: type, label: type.charAt(0).toUpperCase() + type.slice(1) }))
                      ]}
                      placeholder="All types"
                      active={Boolean(setItemFilterType)}
                    />
                    {!isNotesCollectionMode && (
                      <AppSelect
                        className="compact-action-select"
                        value={setItemFilterKey}
                        onChange={setSetItemFilterKey}
                        options={[
                          { value: '', label: 'All keys' },
                          ...Object.keys(setItemKeyGroups).sort().map((root) => ({
                            label: root,
                            options: [
                              { value: `root:${root}`, label: `${root} (All Modes)` },
                              ...[...setItemKeyGroups[root]].sort().map((k) => ({ value: k, label: k }))
                            ]
                          }))
                        ]}
                        placeholder="All keys"
                        active={Boolean(setItemFilterKey)}
                      />
                    )}
                  </>
                )}
              />
            )}
          </div>
        )}
      </div>

      <DetailTimestampsFooter
        createdAt={setData.createdAt}
        updatedAt={setData.updatedAt}
        lastViewedAt={setData.lastViewedAt}
      />

      <div style={{ textAlign: 'center', marginTop: '2rem', paddingBottom: '0.5rem', display: 'flex', justifyContent: 'center', gap: '1.5rem' }}>
        <button
          className="btn-subtle"
          onClick={() => setConfirmDeleteSet(true)}
          style={{ color: '#e03131', fontSize: '0.85rem', opacity: 0.7 }}
        >
          🗑 Delete {isNotesCollectionMode ? 'Folder' : 'Set'}
        </button>
      </div>

      <SendExportModal
        open={showSendPicker}
        onClose={() => setShowSendPicker(false)}
        title={setData?.title}
        user={user}
        onSend={handleSendSet}
        type="set"
        item={setData}
        tunes={allTunes}
      />

      <ActionConfirmModal
        open={confirmDeleteSet}
        title={`Delete ${isNotesCollectionMode ? 'Folder' : 'Set'}`}
        message={`Permanently delete "${setData.title}"? This cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        danger
        onConfirm={handleDeleteSet}
        onCancel={() => setConfirmDeleteSet(false)}
      />

    </PageWrapperAuto>
  );
}
